import React from 'react';
import { useCategoryTreeQuery } from '@/api/queries';
import type { CategoryParentOption } from '@/pages/categories/components/categoriesMeta';
import { CategoryFormModal } from '@/pages/categories/components/categoryFormModal';
import {
    flattenForParentPicker,
    mapTreeNodesToRows,
} from '@/pages/categories/components/categoryTree';
import { useCreateCategory } from '@/pages/categories/useCreateCategory';
import { PATH } from '@/router/PATH';
import type { ActionSurfaceProps } from '../../controlCenterSurfaces';

/**
 * "Add category" from the launcher. See `AddBrandSurface` for the shape.
 *
 * The one of the six that needs data before it can render: the parent picker
 * is the whole tree, indented by depth. It serves itself that tree the way
 * `AddEmployeeSurface` serves itself the tax rates — needing to know the
 * category hierarchy in order to open a launcher is exactly the coupling the
 * surface seam exists to avoid, and `useCategoryTreeQuery` is one unpaginated
 * request with a minute of `staleTime`, fired only while the drawer is open.
 *
 * The rows go through the page's own `mapTreeNodesToRows` with no image
 * resolver and no stats: the picker reads ids, names and nesting, and the
 * figures `/categories/details` carries are the table's business, not this
 * form's. Reusing the mapper rather than walking the nodes here is what keeps
 * the indentation identical on both pages.
 *
 * No exclusion set is passed — that guards against re-parenting a category
 * under itself, which cannot arise when there is nothing being edited.
 */
export const AddCategorySurface: React.FC<ActionSurfaceProps> = ({
    onClose,
    onDone,
}) => {
    const [error, setError] = React.useState<string | null>(null);
    const { create, isCreating } = useCreateCategory();
    const treeQuery = useCategoryTreeQuery();

    const parentOptions = React.useMemo<CategoryParentOption[]>(() => {
        if (!treeQuery.data || !treeQuery.data.success) {
            return [];
        }
        return flattenForParentPicker(
            mapTreeNodesToRows(treeQuery.data.response.items, () => null),
        );
    }, [treeQuery.data]);

    return (
        <CategoryFormModal
            error={error}
            initialValues={null}
            isOpen
            isSubmitting={isCreating}
            mode="create"
            onClose={onClose}
            onSubmit={async (input) => {
                setError(null);
                const created = await create(input);
                if (!created.ok) {
                    setError(created.message);
                    return;
                }
                onDone({
                    messageKey: 'controlCenter.outcome.categoryAdded',
                    messageParams: { name: input.name },
                    to: PATH.CATEGORIES,
                    linkKey: 'controlCenter.outcome.openCategories',
                });
            }}
            parentOptions={parentOptions}
        />
    );
};
